import { calculateSoftCappedMS } from './entityMovement.js';

/**
 * movementPhysics — Single Responsibility: Cinemática de movimiento, vectores de deslizamiento sobre obstáculos y clamping.
 *
 * Contrato público:
 *   updatePosition(entity, dt, obstacleEntities, mapBounds, wallBoxes, bushZones, impassableZones)
 */

export function updatePosition(
  entity,
  dt = 0.016,
  obstacleEntities = [],
  mapBounds = { minX: -14, maxX: 14, minZ: -14, maxZ: 14 },
  wallBoxes = [],
  bushZones = [],
  impassableZones = []
) {
  if (entity.isDead || entity.isStunned) return false;

  if (typeof entity.checkBushZones === 'function') {
    entity.checkBushZones(bushZones);
  }

  if (entity.attackTarget) {
    if (entity.attackTarget.isDead) {
      entity.attackTarget = null;
      entity.isAttacking = false;
    } else if (typeof entity.isInAttackRange === 'function' && entity.isInAttackRange(entity.attackTarget)) {
      entity.targetX = entity.x;
      entity.targetZ = entity.z;
      entity.isMoving = false;
      entity.isSliding = false;
      entity.isAttacking = true;

      const adx = entity.attackTarget.x - entity.x;
      const adz = entity.attackTarget.z - entity.z;
      entity.facingAngle = Math.atan2(adx, adz);
      return false;
    } else {
      entity.targetX = entity.attackTarget.x;
      entity.targetZ = entity.attackTarget.z;
      entity.isAttacking = false;
    }
  } else {
    entity.isAttacking = false;
  }

  const dx = entity.targetX - entity.x;
  const dz = entity.targetZ - entity.z;
  const dist = Math.hypot(dx, dz);

  const epsilon = 0.15;

  if (dist <= epsilon) {
    entity.x = entity.targetX;
    entity.z = entity.targetZ;
    entity.isMoving = false;
    entity.isSliding = false;
    entity.hasActiveCollision = false;
    return false;
  }

  const statsMs = entity.getEffectiveStats ? entity.getEffectiveStats().ms : entity.ms;
  const effectiveMs = calculateSoftCappedMS(statsMs);
  const moveStep = (effectiveMs / 50) * dt;

  const vx = (dx / dist) * Math.min(moveStep, dt ? dist : 0);
  const vz = (dz / dist) * Math.min(moveStep, dt ? dist : 0);

  const clampFn = entity.clampToBounds ? entity.clampToBounds.bind(entity) : (x, z) => [x, z];
  const [nextX, nextZ] = clampFn(entity.x + vx, entity.z + vz, mapBounds);

  const isBlockedAt = (nx, nz, ignoreWall = null, ignoreZone = null) => {
    const ignoreUnits = entity.ignoreUnitCollision === true
      || (Array.isArray(entity.buffs) && entity.buffs.some((b) => b && b.ignoreUnitCollision));
    const hitEntity = !ignoreUnits && obstacleEntities.some(o => {
      if (!o || o.isDead || o === entity || o === entity.attackTarget) return false;
      const cdist = Math.hypot(o.x - nx, o.z - nz);
      const combinedRadius = (entity.radius + o.radius) / 16;
      return cdist < combinedRadius;
    });
    const hitWall = wallBoxes.some(w => w !== ignoreWall && entity.collidesWithBox && entity.collidesWithBox.call({ x: nx, z: nz, radius: entity.radius, isDead: false }, w, 0));
    const hitImpassable = entity.isTerrainPassable ? !entity.isTerrainPassable(nx, nz, impassableZones.filter(z => z !== ignoreZone)) : false;
    return hitEntity || hitWall || hitImpassable;
  };

  if (!isBlockedAt(nextX, nextZ)) {
    entity.x = nextX;
    entity.z = nextZ;
    entity.facingAngle = Math.atan2(dx, dz);
    entity.isMoving = true;
    entity.isSliding = false;
    entity.hasActiveCollision = false;
  } else {
    let nx = 0, nz = 0;

    const hitCircleZone = impassableZones.find(z => z.type === 'circle' && Math.hypot(entity.x - z.x, entity.z - z.z) < z.radius + (entity.radius / 16) + 0.3);

    if (hitCircleZone) {
      const cdx = entity.x - hitCircleZone.x;
      const cdz = entity.z - hitCircleZone.z;
      const clen = Math.hypot(cdx, cdz) || 1;
      nx = cdx / clen;
      nz = cdz / clen;
    } else {
      const hitObs = obstacleEntities.find(o => !o.isDead && o !== entity && o !== entity.attackTarget && Math.hypot(entity.x - o.x, entity.z - o.z) < (entity.radius + o.radius) / 16 + 0.3);
      if (hitObs) {
        const odx = entity.x - hitObs.x;
        const odz = entity.z - hitObs.z;
        const olen = Math.hypot(odx, odz) || 1;
        nx = odx / olen;
        nz = odz / olen;
      } else {
        const collidingWall = wallBoxes.find(w => entity.collidesWithBox && entity.collidesWithBox(w, 0));
        if (collidingWall && entity.getBoxContactNormal) {
          const norm = entity.getBoxContactNormal(collidingWall);
          nx = norm.nx;
          nz = norm.nz;
        }
      }
    }

    if (nx !== 0 || nz !== 0) {
      const dot = vx * nx + vz * nz;
      let slideVx = vx - dot * nx;
      let slideVz = vz - dot * nz;

      if (Math.hypot(slideVx, slideVz) <= 0.001) {
        slideVx = -nz * moveStep;
        slideVz = nx * moveStep;
      }

      const [tangentNextX, tangentNextZ] = clampFn(entity.x + slideVx, entity.z + slideVz, mapBounds);

      const ignoreW = wallBoxes.find(w => entity.collidesWithBox && entity.collidesWithBox(w, 0)) || null;
      const ignoreZ = hitCircleZone || null;

      if (!isBlockedAt(tangentNextX, tangentNextZ, ignoreW, ignoreZ) && Math.hypot(slideVx, slideVz) > 0.001) {
        entity.x = tangentNextX;
        entity.z = tangentNextZ;
        entity.facingAngle = Math.atan2(slideVx, slideVz);
        entity.isMoving = true;
        entity.isSliding = true;
        entity.hasActiveCollision = true;
        return entity.isMoving;
      }
    }

    const [sx, sz_x] = clampFn(entity.x + vx, entity.z, mapBounds);
    const [sx_z, sz] = clampFn(entity.x, entity.z + vz, mapBounds);

    if (!isBlockedAt(sx, sz_x)) {
      entity.x = sx;
      entity.facingAngle = Math.atan2(vx, 0);
      entity.isMoving = true;
      entity.isSliding = true;
    } else if (!isBlockedAt(sx_z, sz)) {
      entity.z = sz;
      entity.facingAngle = Math.atan2(0, vz);
      entity.isMoving = true;
      entity.isSliding = true;
    } else {
      entity.targetX = entity.x;
      entity.targetZ = entity.z;
      entity.isMoving = false;
      entity.isSliding = false;
    }
    entity.hasActiveCollision = true;
  }

  return entity.isMoving;
}
